import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { overlayBunja, useOverlay } from '@flipster/overlay';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { dialog } from '@flipster/react/design-system/dialog';
import { Modal } from '@flipster/react/design-system/modal';
import { createStepComponent } from '@flipster/react/misc/step/Steps';
import { useSteps } from '@flipster/react/misc/step/use-steps';
import { RouterScope } from '@flipster/react/providers/route';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { UsdtPremiumEvent } from '@flipster/utils/segment/events/usdt-premium';

import { authBunja } from '../../../shared/auth/state';
import { usdBalanceEmptyThreshold } from '../../../shared/earn/state';
import { KycDialog } from '../../../shared/kyc/dialog';
import { AccessPremiumEarnModal } from '../access-premium-earn-modal';
import { ManagePusdtModal } from '../manage-pusdt-modal';
import { pusdtAgreementBunja } from '../state';
import InfoStep from './info-step';
import TermsStep from './terms-step';

type AgreementSteps = {
  info: undefined;
  terms: undefined;
};
const Steps = createStepComponent<AgreementSteps>();

interface PusdtNudgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit?: () => void;
}

export const PusdtNudgeModal = ({ isOpen, onClose, onExit }: PusdtNudgeModalProps) => {
  const overlay = useOverlay();
  const { current, push } = useSteps<AgreementSteps>({
    initialValue: {
      step: 'info',
      context: undefined,
    },
  });

  const { pusdtTermAgreedAtom } = useBunja(pusdtAgreementBunja);
  const { walletsAtom } = useBunja(walletsBunja);

  const pusdtTermAgreed = useAtomValue(pusdtTermAgreedAtom);
  const wallets = useAtomValue(walletsAtom);
  const usdtWalletBalance = Num(wallets?.[UsdtCurrency]?.walletBalanceExclWithdrawalPending ?? 0);

  const goToTerms = () => {
    push({ step: 'terms', context: undefined });
  };

  const goToInfo = () => {
    push({ step: 'info', context: undefined });
  };

  const onClickNext = () => {
    segment.track(UsdtPremiumEvent.SubscribeUsdtPremiumClick);
    if (!pusdtTermAgreed) {
      goToTerms();
      return;
    }
    onClose();
    if (usdtWalletBalance.lt(usdBalanceEmptyThreshold)) {
      overlay.open(({ isOpen, onClose, unmount }) => (
        <AccessPremiumEarnModal isOpen={isOpen} onClose={onClose} onExit={unmount} />
      ));
    } else {
      overlay.open(({ isOpen, onClose, unmount }) => (
        <ManagePusdtModal isOpen={isOpen} onClose={onClose} onExit={unmount} />
      ));
    }
  };

  const onClickContinue = () => {
    onClose();
    if (usdtWalletBalance.lt(usdBalanceEmptyThreshold)) {
      overlay.open(({ isOpen, onClose, unmount }) => (
        <AccessPremiumEarnModal isOpen={isOpen} onClose={onClose} onExit={unmount} />
      ));
      return;
    }
    overlay.open(({ isOpen, onClose, unmount }) => (
      <ManagePusdtModal isOpen={isOpen} onClose={onClose} onExit={unmount} />
    ));
  };

  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
      <Steps model={current}>
        <Steps.Step step='info'>{() => <InfoStep onClickNext={onClickNext} closeModal={onClose} />}</Steps.Step>
        <Steps.Step step='terms'>{() => <TermsStep goToInfo={goToInfo} onClose={onClickContinue} />}</Steps.Step>
      </Steps>
    </Modal.Root>
  );
};

export const openPusdtNudgeModalBunja = bunja(() => {
  const overlay = bunja.use(overlayBunja);
  const router = bunja.use(RouterScope);
  const store = bunja.use(JotaiStoreScope);
  const { isLoggedInAtom, isValidKycAtom } = bunja.use(authBunja);

  function openPusdtNudgeModal() {
    if (!store.get(isLoggedInAtom)) {
      router.push('/login');
      return;
    }
    if (!store.get(isValidKycAtom)) {
      dialog.open(({ isOpen, promise }) => <KycDialog isOpen={isOpen} close={promise.resolve} />);
      return;
    }
    overlay.open(({ isOpen, onClose, unmount }) => (
      <PusdtNudgeModal isOpen={isOpen} onClose={onClose} onExit={unmount} />
    ));
  }

  return { openPusdtNudgeModal };
});
openPusdtNudgeModalBunja.debugLabel = 'openPusdtNudgeModalBunja';

export const usePusdtNudgeModal = () => {
  const { openPusdtNudgeModal } = useBunja(openPusdtNudgeModalBunja);
  return openPusdtNudgeModal;
};
